import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

// Top-level menu tree. `to` = direct link, `children` = dropdown subitems.
const menuTree = [
  { label: 'Início', to: '/' },
  {
    label: 'Vistos & Imigração',
    children: [
      { label: 'Tipos de Visto', to: '/categoria/Vistos' },
      { label: 'AIMA & Burocracia', to: '/categoria/AIMA' },
      { label: 'Cidadania Portuguesa', to: '/categoria/Cidadania' },
    ],
  },
  {
    label: 'Morar em Portugal',
    children: [
      { label: 'Custo de Vida', to: '/categoria/Custo de Vida' },
      { label: 'Calculadora de Custo de Vida', to: '/calculadora' },
      { label: 'Moradia', to: '/categoria/Moradia' },
      { label: 'Cidades para Morar', to: '/categoria/Cidades' },
      { label: 'Saúde & Educação', to: '/categoria/Saúde' },
    ],
  },
  {
    label: 'Trabalho & Negócios',
    children: [
      { label: 'Vagas & Carreira', to: '/categoria/Trabalho' },
      { label: 'Empreendedorismo', to: '/categoria/Empreendedorismo' },
    ],
  },
  {
    label: 'Serviços',
    children: [
      { label: 'Nossos Produtos', to: '/produtos' },
      { label: 'Assessorias Parceiras', to: '/servicos' },
    ],
  },
  { label: 'Sobre', to: '/sobre' },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const decodedPath = decodeURIComponent(location.pathname);
  const isAnyChildActive = (children) =>
    children?.some((c) => decodedPath === decodeURIComponent(c.to));

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setOpenMobileSection(null);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* Top bar - logo + auth */}
      <div className="bg-[#E9E5D6]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2 md:py-3">
            <div className="hidden lg:flex items-center gap-3 flex-1" />

            {/* Logo */}
            <div className="flex justify-center">
              <Link to="/" className="block" data-testid="logo-link">
                <img
                  src="https://customer-assets.emergentagent.com/job_visto-portugal/artifacts/1xv4hqe5_Design%20sem%20nome.png"
                  alt="Portugal à Vista"
                  className="h-20 md:h-24 lg:h-28 w-auto block"
                />
              </Link>
            </div>

            {/* Auth (desktop) */}
            <div className="hidden lg:flex items-center gap-3 flex-1 justify-end">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#0F5A34] hover:bg-[#0F5A34]/5"
                      data-testid="user-menu"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#0F5A34] flex items-center justify-center text-white text-sm font-medium">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-['Inter'] text-sm">{user.name}</span>
                      <ChevronDown size={14} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border-[#D6D2C4]">
                    {isAdmin() && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 cursor-pointer" data-testid="admin-link">
                            <Settings size={16} />
                            Painel Admin
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="flex items-center gap-2 cursor-pointer text-red-600"
                      data-testid="logout-btn"
                    >
                      <LogOut size={16} />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login">
                    <Button
                      variant="ghost"
                      className="font-['Inter'] text-sm text-[#1A1A1A] hover:text-[#0F5A34] hover:bg-[#0F5A34]/5"
                      data-testid="login-btn"
                    >
                      Entrar
                    </Button>
                  </Link>
                  <Link to="/registro">
                    <Button
                      className="bg-[#0F5A34] hover:bg-[#0A3F24] font-['Inter'] text-sm px-6"
                      data-testid="register-btn"
                    >
                      Criar Conta
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 text-[#1A1A1A]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Abrir menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop nav */}
      <nav className="hidden lg:block bg-[#0F5A34]" data-testid="desktop-nav">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-1">
            {menuTree.map((item) => {
              const active =
                (item.to && isActive(item.to)) || isAnyChildActive(item.children);
              const baseClass = `px-5 py-3 font-['Montserrat'] text-sm font-medium tracking-wide transition-colors ${
                active
                  ? 'text-[#DFAF3A] bg-black/10'
                  : 'text-white/90 hover:text-[#DFAF3A] hover:bg-black/10'
              }`;

              if (!item.children) {
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={baseClass}
                    data-testid={`nav-${slugify(item.label)}`}
                  >
                    {item.label.toUpperCase()}
                  </Link>
                );
              }

              return (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`${baseClass} inline-flex items-center gap-1.5 outline-none`}
                      data-testid={`nav-${slugify(item.label)}`}
                    >
                      {item.label.toUpperCase()}
                      <ChevronDown size={14} className="opacity-80" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="center"
                    sideOffset={0}
                    className="bg-white border-[#D6D2C4] min-w-[240px] rounded-none shadow-lg p-0"
                  >
                    {item.children.map((c) => (
                      <DropdownMenuItem
                        key={c.to}
                        asChild
                        className="cursor-pointer rounded-none focus:bg-[#0F5A34]/8 focus:text-[#0F5A34]"
                      >
                        <Link
                          to={c.to}
                          className="font-['Inter'] text-sm py-2.5 px-4 w-full"
                          data-testid={`nav-sub-${slugify(c.label)}`}
                        >
                          {c.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Mobile menu - accordion */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[#D6D2C4] max-h-[calc(100vh-90px)] overflow-y-auto" data-testid="mobile-nav">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col">
              {menuTree.map((item) => {
                if (!item.children) {
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={closeMobile}
                      className="font-['Montserrat'] text-sm font-semibold py-3 px-2 text-[#1A1A1A] hover:bg-[#E9E5D6] rounded-sm border-b border-[#D6D2C4] last:border-b-0"
                      data-testid={`mobile-nav-${slugify(item.label)}`}
                    >
                      {item.label}
                    </Link>
                  );
                }
                const isOpen = openMobileSection === item.label;
                return (
                  <div key={item.label} className="border-b border-[#D6D2C4] last:border-b-0">
                    <button
                      onClick={() => setOpenMobileSection(isOpen ? null : item.label)}
                      className="w-full flex items-center justify-between font-['Montserrat'] text-sm font-semibold py-3 px-2 text-[#1A1A1A] hover:bg-[#E9E5D6] rounded-sm"
                      aria-expanded={isOpen}
                      data-testid={`mobile-nav-${slugify(item.label)}`}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 text-[#0F5A34] ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {isOpen && (
                      <div className="pb-2 pl-4 flex flex-col">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            onClick={closeMobile}
                            className="font-['Inter'] text-sm py-2 px-2 text-[#3F3F3F] hover:bg-[#E9E5D6] rounded-sm border-l-2 border-[#DFAF3A] ml-1 pl-3"
                            data-testid={`mobile-nav-sub-${slugify(c.label)}`}
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Auth on mobile */}
            <div className="border-t border-[#D6D2C4] mt-3 pt-3">
              {user ? (
                <>
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      onClick={closeMobile}
                      className="font-['Montserrat'] text-sm font-medium py-3 px-2 text-[#0F5A34] hover:bg-[#E9E5D6] rounded-sm flex items-center gap-2"
                    >
                      <Settings size={16} />
                      Painel Admin
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); closeMobile(); }}
                    className="w-full font-['Montserrat'] text-sm font-medium py-3 px-2 text-red-600 text-left hover:bg-red-50 rounded-sm flex items-center gap-2"
                  >
                    <LogOut size={16} />
                    Sair
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 pt-1">
                  <Link to="/login" onClick={closeMobile}>
                    <Button variant="outline" className="w-full border-[#0F5A34] text-[#0F5A34]">
                      Entrar
                    </Button>
                  </Link>
                  <Link to="/registro" onClick={closeMobile}>
                    <Button className="w-full bg-[#0F5A34] hover:bg-[#0A3F24]">
                      Criar Conta
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
